import "../style/push-consents-popup.scss"

import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { hidePushConsentsPopup } from "@app/store/app/appActions"
import { RootState } from "@app/types"
import { isChristmasCampaignTime } from "../../../helpers/datePeriods"

// Components
import Button from "@ng-mw/reol/components/Button"
import Popup from "@modules/popup/Popup"
import Container, { Content, Padding } from "@modules/shared/Container"
import UserConsents from "../UserConsents"
import { SubHeader } from "@modules/framework/AppHeader"
import { getWhitelistByChannel } from "../helpers/getWhitelistByChannel"
import PushConsentTypes from "../const/PushConsentTypes"
import { setConsideredPushNotifications } from "@ng-mw/shared-react-components/push-snackbar"

export const PushConsentsPopup = () => {
    const dispatch = useDispatch<any>()
    const isOpen = useSelector((state: RootState) => state.app.pushConsentsPopup.visible)
    const pushNotifications = useSelector((state: RootState) => state.pushNotifications)
    const data = pushNotifications?.data
    const pushConsentActive = data?.consentActive
    const [doOpen, setDoOpen] = useState(false)

    const consents = useSelector((state: RootState) => state.consents)
    const consentsData = consents.data
    const consentsLoaded = consents.consentsInitialized && !consents.loading

    const { anyConsentsIsOff } = useMemo(() => {
        const consents = consentsData.filter(consent => consent.itemType === "CONSENT")

        const whiteListedTypes = isChristmasCampaignTime(new Date())
            ? ["JUL", "FORDEL"]
            : ["FORDEL"]
        const whiteListedEmailChannels = ["E-Mail"]
        const whiteListedSMSChannels = ["SMS"]

        const emailConsentsWhitelist = getWhitelistByChannel(
            consents,
            whiteListedEmailChannels,
            whiteListedTypes,
        )
        const smsConsentsWhitelist = getWhitelistByChannel(
            consents,
            whiteListedSMSChannels,
            whiteListedTypes,
        )
        const pushWhitelist = getWhitelistByChannel(consents, ["Push"], PushConsentTypes)
        const allFiltered = [...pushWhitelist, ...smsConsentsWhitelist, ...emailConsentsWhitelist]

        return {
            anyConsentsIsOff: allFiltered.some(c => c.status !== true),
        }
    }, [consentsData])

    const handleClose = () => {
        setConsideredPushNotifications(new Date())
        dispatch(hidePushConsentsPopup())
    }

    useEffect(() => {
        if (!isOpen) {
            setDoOpen(false)
            return
        }

        // Let the user explicitly close an already visible popup. That is where the
        // considered timestamp is stored.
        if (doOpen) {
            return
        }

        // `undefined` is the normal state while the iOS bridge initializes.
        // Do not interpret it as notifications being disabled.
        if (!consentsLoaded || pushConsentActive === undefined) {
            return
        }

        if (anyConsentsIsOff || !pushConsentActive) {
            setDoOpen(true)
            return
        }

        dispatch(hidePushConsentsPopup())
    }, [anyConsentsIsOff, consentsLoaded, dispatch, doOpen, isOpen, pushConsentActive])

    return (
        <Popup
            isOpen={isOpen && doOpen}
            isForcedFullscreen
            popupAnimation="slide-left"
            buttons={
                <>
                    <Button
                        text="Bekreft"
                        onClick={handleClose}
                        isDisabled={!pushConsentActive}
                    />
                    {!pushConsentActive ? (
                        <Button
                            text="Hopp over"
                            isCancel
                            onClick={handleClose}
                        />
                    ) : null}
                </>
            }
        >
            <SubHeader title="Hold deg oppdatert med varslinger" />
            <UserConsents
                isConsentsOnlyVisibleIfStatusFalse
                isExternalLinkHidden
            />
            <Container
                className="push-consents-popup__colophon"
                padding={Padding.None}
                content={Content.Padding}
            >
                Du kan når som helst reservere deg mot å motta app-varsler under dine innstillinger
                her i appen eller i dine telefoninnstillinger
            </Container>
        </Popup>
    )
}

export default PushConsentsPopup
